import React from "react";
import {
  AlertBox,
  Button,
  PrivacyPolicyLink,
  TermsOfServiceLink,
  AgeGatedPressable,
} from "@gonoodle/gn-universe-ui";
import { Form, Formik } from "formik";
import Image from "next/legacy/image";
import { useRouter } from "next/router";

import { Link, withAuthorization } from "../containers";
import Layout from "../components/Layout";
import { ROUTE_PERMISSIONS, ROUTE_PREFIX } from "../constants";
import { EmailField } from "../components/FormElements";
import { useForgotPassword } from "../hooks";
import { getUtmParamsFromQueryParams } from "../utils/analytics";

const ForgotPasswordForm = () => {
  const router = useRouter();
  const { forgotPassword, isLoading, isSuccess, error } = useForgotPassword();

  return (
    <div className="md:w-screen-xs space-y-md">
      {error && error.response.status === 404 ? (
        <AlertBox className="p-sm" text="Sorry, that email is not valid." />
      ) : null}

      {error && error.response.status !== 404 ? (
        <AlertBox
          className="p-sm"
          text="Something went wrong. Please try again."
        />
      ) : null}

      <div className="flex flex-col p-lg bg-white rounded-lg">
        {isSuccess ? (
          <>
            <div className="text-center space-y-md">
              <h1 className="gn-headline-sm">
                We just sent you an email with instructions to reset your
                password.
              </h1>
              <div className="gn-text-sm text-center pt-sm">
                <p>Didn’t get the email?</p>
                <p>1. Check those pesky spam folders.</p>
                <AgeGatedPressable
                  className="underline"
                  onPress={() =>
                    new Promise(() => {
                      router.push("https://support.gonoodle.com");
                    })
                  }
                  onAbort={() => router.push(ROUTE_PREFIX.HOME)}
                >
                  2. Contact GoNoodle support.
                </AgeGatedPressable>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="text-center pb-md space-y-sm">
              <h1 className="gn-headline-md">Forgot your password?</h1>
              <p className="gn-text-sm font-bold">
                We&apos;ll send you an email with instructions.
              </p>
            </div>
            <Formik
              onSubmit={({ email }) => forgotPassword(email)}
              initialValues={{
                email: undefined,
              }}
            >
              {() => (
                <Form noValidate className="space-y-md">
                  <div className="space-y-sm">
                    <EmailField
                      name="email"
                      label="Email address"
                      autoFocus={true}
                    />
                  </div>
                  <Button
                    variant="vivid"
                    size="lg"
                    disabled={isLoading}
                    type="submit"
                    className="w-full"
                  >
                    Next
                  </Button>
                  <div className="space-y-sm text-center gn-text-sm text-gray-800">
                    <p>Requested an email but never received it? </p>
                    <AgeGatedPressable
                      className="underline"
                      onPress={() =>
                        new Promise(() => {
                          router.push("https://support.gonoodle.com");
                        })
                      }
                      onAbort={() => router.push(ROUTE_PREFIX.HOME)}
                    >
                      Contact GoNoodle support.
                    </AgeGatedPressable>
                  </div>
                </Form>
              )}
            </Formik>
          </>
        )}
      </div>
      <div className="flex flex-row justify-center space-x-sm text-white font-regular gn-text-xs">
        <PrivacyPolicyLink />
        <p> | </p>
        <TermsOfServiceLink />
      </div>
    </div>
  );
};

export default function ForgotPasswordPage() {
  const router = useRouter();
  return (
    <Layout
      pageTitle="Forgot Password"
      pageDescription="Forgot Password Page"
      footer={false}
      header={false}
    >
      <>
        <nav className="gn-nav">
          <div className="flex items-center justify-center h-navbar">
            <div className="transform transition hover:scale-110">
              <Link
                href={ROUTE_PREFIX.HOME}
                events={[
                  {
                    event: "Link Clicked",
                    properties: {
                      title: "GoNoodle Logo",
                      sourceElement: "nav",
                      sourceName: "nav",
                      ...getUtmParamsFromQueryParams(router.query),
                    },
                  },
                ]}
              >
                <span className="sr-only">GoNoodle</span>
                <Image
                  width={150}
                  height={25}
                  src="/GoNoodleLogo_Registered_Reverse.svg"
                  alt="GoNoodle logo"
                />
              </Link>
            </div>
          </div>
        </nav>
        <div className="w-full flex flex-col items-center px-sm py-lg">
          <ForgotPasswordForm />
        </div>
      </>
    </Layout>
  );
}

export const getServerSideProps = withAuthorization((ctx, user) => {
  if (user.features?.registration !== true) {
    return { redirect: { destination: "/", permanent: false } };
  }
  return {
    props: {},
  };
}, ROUTE_PERMISSIONS.ANONYMOUS);
